import React, { useState } from 'react';
import { ClipboardDocumentIcon, CheckCircleIcon, ClockIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

interface AssignmentContentProps {
  details: string;
  estimatedDuration?: number; // in minutes
  resources?: string[];
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
}

const AssignmentContent: React.FC<AssignmentContentProps> = ({
  details,
  estimatedDuration = 30,
  resources = [],
  onProgress,
  onComplete
}) => {
  const [progress, setProgress] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [submission, setSubmission] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleMarkProgress = (newProgress: number) => {
    setProgress(newProgress);
    onProgress?.(newProgress);
    
    if (newProgress >= 100) {
      setIsCompleted(true);
      onComplete?.();
    }
  };

  const handleSubmitAssignment = () => {
    if (submission.trim()) {
      setSubmitted(true);
      setProgress(100);
      setIsCompleted(true);
      onProgress?.(100);
      onComplete?.();
    }
  };

  return (
    <div className="space-y-4">
      {/* Assignment Header */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ClipboardDocumentIcon className="h-6 w-6 text-purple-600" />
            <div>
              <h3 className="font-semibold text-purple-900 text-sm sm:text-base">Assignment</h3>
              <p className="text-purple-700 text-xs sm:text-sm">
                Estimated completion time: {estimatedDuration} minutes
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-purple-600">Time spent</div>
            <div className="text-sm font-medium text-purple-900">{Math.floor(timeSpent / 60)}:{(timeSpent % 60).toString().padStart(2, '0')}</div>
          </div>
        </div>
      </div>

      {/* Assignment Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
        <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-3">Assignment Details</h4>
        <div className="prose prose-sm sm:prose-base max-w-none text-gray-700">
          {details}
        </div>
      </div>

      {/* Resources */}
      {resources.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 text-sm sm:text-base mb-3">📚 Resources</h4>
          <div className="space-y-2">
            {resources.map((resource, index) => (
              <div key={index} className="flex items-center space-x-2">
                <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                <a
                  href={resource}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 hover:text-blue-900 text-sm underline"
                >
                  Resource {index + 1}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assignment Submission */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-semibold text-green-900 text-sm sm:text-base mb-3">📝 Assignment Submission</h4>
        
        {!submitted ? (
          <div className="space-y-3">
            <textarea
              value={submission}
              onChange={(e) => setSubmission(e.target.value)}
              placeholder="Describe your work, paste links, or provide any relevant information..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              rows={4}
            />
            <button
              onClick={handleSubmitAssignment}
              disabled={!submission.trim()}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Submit Assignment
            </button>
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-green-700">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="font-medium">Assignment submitted successfully!</span>
          </div>
        )}
      </div>

      {/* Progress Tracking */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Assignment Progress</h4>
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-4 w-4 text-gray-500" />
            <span className="text-xs text-gray-600">{Math.floor(timeSpent / 60)}:{(timeSpent % 60).toString().padStart(2, '0')}</span>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">{progress.toFixed(1)}% completed</span>
          
          {!isCompleted && (
            <div className="flex space-x-2">
              <button
                onClick={() => handleMarkProgress(25)}
                className="px-2 py-1 bg-primary-600 text-white text-xs font-medium rounded hover:bg-primary-700 transition-colors"
              >
                25%
              </button>
              <button
                onClick={() => handleMarkProgress(50)}
                className="px-2 py-1 bg-primary-600 text-white text-xs font-medium rounded hover:bg-primary-700 transition-colors"
              >
                50%
              </button>
              <button
                onClick={() => handleMarkProgress(75)}
                className="px-2 py-1 bg-primary-600 text-white text-xs font-medium rounded hover:bg-primary-700 transition-colors"
              >
                75%
              </button>
              <button
                onClick={() => handleMarkProgress(100)}
                className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Complete
              </button>
            </div>
          )}
          
          {isCompleted && (
            <div className="flex items-center text-green-600">
              <CheckCircleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm font-medium">Completed!</span>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 text-sm sm:text-base mb-2">💡 Assignment Tips</h4>
        <ul className="text-yellow-800 text-xs sm:text-sm space-y-1">
          <li>• Read the assignment details carefully</li>
          <li>• Use the provided resources to help you</li>
          <li>• Track your progress as you work</li>
          <li>• Submit your work when complete</li>
          <li>• You can mark progress manually if needed</li>
        </ul>
      </div>
    </div>
  );
};

export default AssignmentContent;
